// ─── Helpers partilhados pelos ecrãs Manage/ ────────────────────────────
//
// Padrões comuns que se repetiam em todos os ecrãs CRUD da Manutenção:
//  · Form de edição aparece no topo da página → utilizador perde-se
//    quando clica "Editar" num item da lista mais abaixo
//  · Preços mostrados sem moeda → operador AO confunde com EUR
//
// Centralizar aqui evita 5+ implementações ligeiramente diferentes.

import { useEffect, useRef } from 'react';

// Lista canónica dos tenants que correm o fluxo Petrotec (BM, H.N./H.E.,
// clientes finais, postos, equipamentos, stock, SLA contratual…).
// Identificados por `clientExternalId` para serem estáveis entre dev/
// staging/prod (o clientId numérico muda; o external do EcooGPS não).
//
//   · '20'  → Petrotec PT  (Francisco Pires, client_id local = 10)
//   · '918' → Petrotec AO  (Andriy Sandulyak, client_id local = 17)
//
// Para adicionar um 3º tenant Petrotec basta acrescentar aqui — toda a
// UI que faça `isPetrotecTenant(externalId)` passa automaticamente a
// reconhecê-lo, sem caçar `=== '918'` espalhados pelo código.
export const PETROTEC_TENANT_EXTERNAL_IDS = ['20', '918'];
// Passa a ser conduzido pelo PERFIL (clientSettings.uiVariant). A lista de
// externalId fica só como fallback de transição: quando o perfil ainda não tem
// uiVariant explícito (='generic' ou ausente), reconhece os tenants antigos.
export function isPetrotecTenant(externalId, clientSettings) {
  const v = clientSettings?.uiVariant;
  if (v && v !== 'generic') return v === 'petrotec';
  return PETROTEC_TENANT_EXTERNAL_IDS.includes(String(externalId || ''));
}

// ── Move Express (distribuição de combustível) ──────────────────────
// Frota com tractores + cisternas semi-reboque. UI distinta — coluna
// "Capacidade", painel KPI de cisternas, sub-selector no dispatch.
//
//   · '8028' → João Paulo Move Express (client_id local = 8)
//
// Acrescentar aqui se houver outro tenant fuel no futuro.
export const MOVE_EXPRESS_TENANT_EXTERNAL_IDS = ['8028'];
// Conduzido pelo PERFIL (clientSettings.uiVariant==='fuel'). Qualquer cliente
// com um perfil 'fuel' (Move Express, Transfuel, …) ganha a UI de combustível
// sem tocar no código. Lista de externalId = fallback de transição.
export function isMoveExpressTenant(externalId, clientSettings) {
  const v = clientSettings?.uiVariant;
  if (v && v !== 'generic') return v === 'fuel';
  return MOVE_EXPRESS_TENANT_EXTERNAL_IDS.includes(String(externalId || ''));
}

// ── Catálogo único de variantes de UI ───────────────────────────────
// Fonte de verdade partilhada pelo backoffice: dropdown do Perfil + selos.
// Para adicionar uma variante nova: (1) acrescenta uma entrada aqui,
// (2) acrescenta o valor ao enum em server/src/schemas/clientProfile.schema.js
// (rebuild do backend), (3) liga o comportamento no planner (helper + ramo,
// ex.: dashboard no AppLayout). O JSON extraSettings.uiVariant não precisa
// de migração de BD.
export const UI_VARIANTS = {
  generic: { key: 'generic', label: 'Genérica (base)', short: 'Genérica', icon: '🧩', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  fuel: { key: 'fuel', label: 'Combustível (Move Express, Transfuel…)', short: 'Combustível', icon: '⛽', color: '#c2410c', bg: '#fff1e3', border: '#fdd9bd' },
  petrotec: { key: 'petrotec', label: 'Petrotec (assistência técnica)', short: 'Petrotec', icon: '🔧', color: '#4338ca', bg: '#eeeefc', border: '#dcdcfa' },
  mercadoria: { key: 'mercadoria', label: 'Mercadoria (entregas / distribuição)', short: 'Mercadoria', icon: '📦', color: '#0369a1', bg: '#e0f2fe', border: '#bae6fd' },
};

export function uiVariantMeta(v) {
  return UI_VARIANTS[v] || UI_VARIANTS.generic;
}

/**
 * Hook que devolve uma ref para anexar ao card de edição e faz
 * scrollIntoView automático sempre que `editingId` muda para um valor
 * não-null. Comporta-se igual ao já implementado em AccountingCodesView.
 *
 * Uso:
 *   const formCardRef = useFormCardScroll(editingId);
 *   ...
 *   {editingId !== null && (
 *     <Box ref={formCardRef} className={classes.formCard}> … </Box>
 *   )}
 */
export function useFormCardScroll(editingId) {
  const ref = useRef(null);
  useEffect(() => {
    if (editingId !== null && ref.current) {
      requestAnimationFrame(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [editingId]);
  return ref;
}

const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'Fr.',
  AOA: 'Kz', NAD: 'N$', ZAR: 'R', MZN: 'MT', CVE: '$', STN: 'Db',
  XOF: 'CFA', XAF: 'CFA', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  DKK: 'kr', SEK: 'kr', NOK: 'kr', ISK: 'kr',
};

/**
 * Devolve { code, symbol } da moeda do tenant. Lê do clientSettings.
 * Útil para mostrar nos labels e valores guardados em colunas Decimal
 * sem coluna `currency` (accounting_codes, stock_items, etc.).
 */
export function getTenantCurrency(clientSettings) {
  const code = clientSettings?.defaultCurrency || 'EUR';
  return { code, symbol: CURRENCY_SYMBOLS[code] || code };
}
